<template>
  <transition name="totp-reset-dialog">
    <div
      v-if="visible"
      class="totp-reset-dialog"
      @keydown.esc="cancel"
    >
      <div
        class="totp-reset-dialog__backdrop"
        @click="cancel"
      />
      <section
        class="totp-reset-dialog__panel"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
      >
        <button
          type="button"
          class="totp-reset-dialog__close"
          :aria-label="$t('Cancel')"
          @click="cancel"
        >
          <feather-icon
            icon="XIcon"
            size="18"
          />
        </button>

        <div class="totp-reset-dialog__icon">
          <feather-icon
            icon="RefreshCwIcon"
            size="24"
          />
        </div>

        <h3
          :id="titleId"
          class="totp-reset-dialog__title"
        >
          {{ $t('Reset two-factor authentication?') }}
        </h3>

        <p class="totp-reset-dialog__message">
          {{ $t('Enter your 6-digit verification code to reset {username}', { username }) }}
        </p>

        <div
          class="totp-reset-dialog__digits"
          dir="ltr"
        >
          <input
            v-for="(_, index) in digits"
            :key="index"
            ref="digitInput"
            class="totp-reset-dialog__digit"
            :value="digits[index]"
            inputmode="numeric"
            maxlength="1"
            autocomplete="one-time-code"
            :disabled="submitting"
            :aria-label="`${$t('Verification code')} ${index + 1}`"
            @input="onDigitInput($event, index)"
            @keydown="onDigitKeydown($event, index)"
            @paste.prevent="onPaste($event, index)"
          >
        </div>

        <small
          v-if="error"
          class="totp-reset-dialog__error"
        >
          {{ error }}
        </small>

        <div class="totp-reset-dialog__actions">
          <button
            type="button"
            class="totp-reset-dialog__button totp-reset-dialog__button--secondary"
            :disabled="submitting"
            @click="cancel"
          >
            {{ $t('Cancel') }}
          </button>
          <button
            type="button"
            class="totp-reset-dialog__button totp-reset-dialog__button--primary"
            :disabled="!isComplete || submitting"
            @click="confirm"
          >
            {{ submitting ? $t('Resetting') : $t('Reset') }}
          </button>
        </div>
      </section>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'TotpResetDialog',
  data() {
    return {
      visible: false,
      username: '',
      digits: ['', '', '', '', '', ''],
      error: '',
      submitting: false,
      submitHandler: null,
      errorMessageHandler: null,
      resolveDialog: null,
      previousActiveElement: null,
      titleId: `totp-reset-title-${Math.random().toString(36).slice(2)}`,
    }
  },
  computed: {
    code() {
      return this.digits.join('')
    },
    isComplete() {
      return this.code.length === 6
    },
  },
  beforeDestroy() {
    this.finish(false)
  },
  methods: {
    open(options) {
      if (this.resolveDialog) this.finish(false)

      this.username = typeof options === 'string' ? options : options.username
      this.digits = ['', '', '', '', '', '']
      this.error = ''
      this.submitting = false
      this.submitHandler = typeof options === 'string' ? null : options.submit
      this.errorMessageHandler = typeof options === 'string' ? null : options.getErrorMessage
      this.visible = true
      this.previousActiveElement = document.activeElement

      this.$nextTick(() => {
        this.focusDigit(0)
      })

      return new Promise(resolve => {
        this.resolveDialog = resolve
      })
    },
    async confirm() {
      if (this.submitting) return

      if (!this.isComplete) {
        this.error = this.$t('Please enter a 6-digit verification code')
        this.focusDigit(this.digits.findIndex(value => !value))
        return
      }

      if (!this.submitHandler) {
        this.finish(true)
        return
      }

      this.submitting = true
      this.error = ''
      try {
        await this.submitHandler(this.code)
        this.finish(true)
      } catch (error) {
        this.error = this.getErrorMessage(error)
        this.digits = ['', '', '', '', '', '']
        this.$nextTick(() => this.focusDigit(0))
      } finally {
        this.submitting = false
      }
    },
    cancel() {
      if (this.submitting) return
      this.finish(false)
    },
    finish(value) {
      if (!this.visible && !this.resolveDialog) return

      this.visible = false
      const resolve = this.resolveDialog
      this.resolveDialog = null
      this.submitHandler = null
      this.errorMessageHandler = null

      this.$nextTick(() => {
        if (this.previousActiveElement && this.previousActiveElement.focus) {
          this.previousActiveElement.focus()
        }
        this.previousActiveElement = null
      })

      if (resolve) resolve(value)
    },
    getErrorMessage(error) {
      if (this.errorMessageHandler) return this.errorMessageHandler(error)
      return this.$t('Unable to reset two-factor authentication. Please try again')
    },
    onDigitInput(event, index) {
      if (this.submitting) return

      const rawValue = event.target.value || ''
      const digitValue = rawValue.replace(/\D/g, '')

      if (digitValue.length > 1) {
        this.fillDigits(digitValue, index)
        return
      }

      this.$set(this.digits, index, digitValue)
      this.error = ''

      if (digitValue && index < this.digits.length - 1) {
        this.focusDigit(index + 1)
      }
    },
    onDigitKeydown(event, index) {
      if (this.submitting) {
        event.preventDefault()
        return
      }

      if (event.metaKey || event.ctrlKey) return

      if (event.key === 'Enter') {
        event.preventDefault()
        this.confirm()
        return
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        this.focusDigit(Math.max(index - 1, 0))
        return
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault()
        this.focusDigit(Math.min(index + 1, this.digits.length - 1))
        return
      }

      if (event.key === 'Backspace' && !this.digits[index] && index > 0) {
        event.preventDefault()
        this.focusDigit(index - 1)
        return
      }

      const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Home', 'End']
      if (allowedKeys.includes(event.key)) return
      if (/^\d$/.test(event.key)) return

      event.preventDefault()
    },
    onPaste(event, index) {
      if (this.submitting) return

      const text = event.clipboardData ? event.clipboardData.getData('text') : ''
      this.fillDigits(text, index)
    },
    fillDigits(value, startIndex) {
      if (this.submitting) return

      const numbers = `${value || ''}`.replace(/\D/g, '').slice(0, this.digits.length)
      if (!numbers) return

      const next = [...this.digits]
      for (let i = 0; i < numbers.length && startIndex + i < next.length; i += 1) {
        next[startIndex + i] = numbers[i]
      }

      this.digits = next
      this.error = ''
      this.focusDigit(Math.min(startIndex + numbers.length, this.digits.length - 1))
    },
    focusDigit(index) {
      this.$nextTick(() => {
        const inputs = this.$refs.digitInput
        if (!inputs || !inputs[index]) return
        inputs[index].focus()
        inputs[index].select()
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.totp-reset-dialog {
  position: fixed;
  z-index: 10020;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
}

.totp-reset-dialog__backdrop {
  position: absolute;
  inset: 0;
  background-color: rgba(32, 29, 36, 0.44);
  backdrop-filter: blur(2px);
}

.totp-reset-dialog__panel {
  position: relative;
  width: min(100%, 430px);
  padding: 1.65rem;
  border: 1px solid rgba(var(--hdg-brand-primary-rgb), 0.1);
  border-radius: 10px;
  background-color: #fff;
  box-shadow: 0 18px 55px rgba(34, 22, 28, 0.22);
  text-align: left;
}

.totp-reset-dialog__close {
  position: absolute;
  top: 0.85rem;
  right: 0.85rem;
  display: inline-flex;
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #8a8086;
  cursor: pointer;

  &:hover,
  &:focus {
    background-color: rgba(var(--hdg-brand-primary-rgb), 0.08);
    color: var(--hdg-brand-primary);
    outline: none;
  }
}

.totp-reset-dialog__icon {
  display: inline-flex;
  width: 3rem;
  height: 3rem;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  border: 1px solid rgba(var(--hdg-brand-primary-rgb), 0.22);
  border-radius: 9px;
  background-color: rgba(var(--hdg-brand-primary-rgb), 0.08);
  color: var(--hdg-brand-primary);
}

.totp-reset-dialog__title {
  margin: 0;
  color: #5a5358;
  font-size: 1.45rem;
  font-weight: 700;
  line-height: 1.25;
}

.totp-reset-dialog__message {
  margin: 0.45rem 0 0;
  color: #81777e;
  font-size: 0.98rem;
  line-height: 1.5;
}

.totp-reset-dialog__digits {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 0.45rem;
  margin-top: 1.25rem;
}

.totp-reset-dialog__digit {
  width: 100%;
  height: 3.15rem;
  min-width: 0;
  padding: 0;
  border: 1px solid #e8dce0;
  border-radius: 7px;
  background-color: #fff;
  color: #554d53;
  font-size: 1.3rem;
  font-weight: 700;
  line-height: 1;
  text-align: center;

  &:focus {
    border-color: var(--hdg-brand-primary);
    outline: none;
    box-shadow: 0 0 0 0.18rem rgba(var(--hdg-brand-primary-rgb), 0.11);
  }

  &:disabled {
    background-color: #f8f5f6;
    cursor: wait;
    opacity: 0.72;
  }
}

.totp-reset-dialog__error {
  display: block;
  margin-top: 0.75rem;
  color: #ea5455;
  font-size: 0.9rem;
}

.totp-reset-dialog__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-top: 1.45rem;
}

.totp-reset-dialog__button {
  display: inline-flex;
  min-height: 2.9rem;
  align-items: center;
  justify-content: center;
  padding: 0.7rem 1rem;
  border-radius: 7px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, opacity 0.15s ease;

  &:disabled {
    cursor: not-allowed;
  }
}

.totp-reset-dialog__button--primary {
  border: 1px solid var(--hdg-brand-primary);
  background-color: var(--hdg-brand-primary);
  color: #fff;

  &:hover,
  &:focus {
    border-color: var(--hdg-brand-primary-active);
    background-color: var(--hdg-brand-primary-active);
    outline: none;
  }

  &:disabled {
    border-color: rgba(var(--hdg-brand-primary-rgb), 0.32);
    background-color: rgba(var(--hdg-brand-primary-rgb), 0.42);
    cursor: not-allowed;
  }
}

.totp-reset-dialog__button--secondary {
  border: 1px solid #9b9097;
  background-color: #fff;
  color: #665d64;

  &:hover,
  &:focus {
    border-color: var(--hdg-brand-primary);
    color: var(--hdg-brand-primary);
    outline: none;
  }

  &:disabled {
    border-color: #d5ccd1;
    color: #aaa1a7;
    background-color: #f8f5f6;
  }
}

.totp-reset-dialog-enter-active,
.totp-reset-dialog-leave-active {
  transition: opacity 0.16s ease;
}

.totp-reset-dialog-enter,
.totp-reset-dialog-leave-to {
  opacity: 0;
}

.totp-reset-dialog-enter-active .totp-reset-dialog__panel,
.totp-reset-dialog-leave-active .totp-reset-dialog__panel {
  transition: transform 0.16s ease, opacity 0.16s ease;
}

.totp-reset-dialog-enter .totp-reset-dialog__panel,
.totp-reset-dialog-leave-to .totp-reset-dialog__panel {
  opacity: 0;
  transform: translateY(8px) scale(0.98);
}

@media (max-width: 575.98px) {
  .totp-reset-dialog {
    padding: 1rem;
  }

  .totp-reset-dialog__panel {
    padding: 1.35rem;
  }

  .totp-reset-dialog__digit {
    height: 2.8rem;
    font-size: 1.15rem;
  }

  .totp-reset-dialog__actions {
    grid-template-columns: 1fr;
  }
}
</style>
